import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import type { PaletteMode } from "@mui/material/styles";
import { darkTheme, lightTheme } from "./theme";

const STORAGE_KEY = "ankhos.colorMode";

interface ColorMode {
  mode: PaletteMode;
  toggle: () => void;
}

const Ctx = createContext<ColorMode>({ mode: "light", toggle: () => undefined });

function stored(): PaletteMode | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "dark" || v === "light" ? v : null;
  } catch {
    return null;
  }
}

function preferred(): PaletteMode {
  // Light is the console default (scheme A). The OS preference only wins when
  // the operator has never chosen; a stored choice always wins over the OS.
  try {
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** App-wide colour mode: a stored choice, else the OS preference, else light. */
export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>(() => stored() ?? preferred());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* a browser refusing storage must not break the app */
    }
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const toggle = useCallback(() => setMode((m) => (m === "dark" ? "light" : "dark")), []);
  const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

  return (
    <Ctx.Provider value={value}>
      <ThemeProvider theme={mode === "dark" ? darkTheme : lightTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </Ctx.Provider>
  );
}

export function useColorMode(): ColorMode {
  return useContext(Ctx);
}

/**
 * Pins a subtree to one mode regardless of the operator's choice. The
 * marketing pages (landing, pricing) are pinned dark: short visits, big type,
 * and the amber call to action is the brightest thing on the page. The
 * console is never pinned -- long reading and invoices want light by default.
 */
export function PinnedMode({ mode, children }: { mode: PaletteMode; children: ReactNode }) {
  return (
    <ThemeProvider theme={mode === "dark" ? darkTheme : lightTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
